<template>
	<ja-modal
		ref="modal"
		class="category-modal"
		:show="show"
		tabindex="0"
		@close-modal="closeModal"
	>
		<ja-building-header
			class="category-header"
			:asset="`categories/${category.assetName}`"
			:billboard-asset="billboard"
		></ja-building-header>
		<content>
			<ja-icon>
				<svgicon :name="iconName"></svgicon>
			</ja-icon>
			<h1 class="heading-5">{{ category.name }}</h1>
			<h2 class="heading-1">{{ subtitle }}</h2>
			<h3 class="body-text-1">{{ content }}</h3>
			<div v-if="isPasscodeRequired" class="passcode">
				<ja-input
					v-model="userPasscode"
					class="mt--1"
					name="passcode"
					:label="$t('passcodeInputLabel')"
					:placeholder="$t('passcodeInputPlaceholder')"
					:max-length="3"
					is-number-input
					:external-errors="passcodeValidationErrors"
				></ja-input>
				<ja-button
					class="small right"
					name="next-modal"
					:disabled="userPasscode.length < 3"
					:aria-label="$t('navigateCategory', { category: category.name })"
					@click.native="validatePasscode"
				>
					<span class="button-text">{{ $t('next') }}</span>
					<svgicon name="arrow-right"></svgicon>
				</ja-button>
			</div>

			<ja-button
				v-else
				class="small right"
				name="next-modal"
				:aria-label="$t('navigateCategory', { category: category.name })"
				@click.native="openResearch"
			>
				<span class="button-text">{{ $t('next') }}</span>
				<svgicon name="arrow-right"></svgicon>
			</ja-button>
		</content>
		<ja-close-button
			name="close"
			@click.native="closeModal"
			@keydown.esc="closeModal"
		></ja-close-button>
	</ja-modal>
</template>
<script>
import Button from '../ui/Button';
import Icon from '../ui/Icon';
import BuildingHeader from '../ui/headers/BuildingHeader';
import '../icons/arrow-right';
import '../icons/modal-close';
import Modal from './Modal';
import CloseButton from '../ui/CloseButton';
import ModalEvents from '@/mixins/ModalEvents';
import { Input } from '@/components/ui/form';
import ExpenseCategoryService from '@/services/ExpenseCategoryService';
import UserData from '@/mixins/UserData';
import { applicationLoanTypes } from '@/helpers/constants';

export default {
	components: {
		jaButton: Button,
		jaModal: Modal,
		jaIcon: Icon,
		jaBuildingHeader: BuildingHeader,
		jaCloseButton: CloseButton,
		jaInput: Input
	},
	mixins: [ModalEvents, UserData],
	props: {
		category: {
			type: Object,
			default: () => {
				return {};
			}
		},
		show: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			title: '',
			subtitle: '',
			content: '',
			iconName: '',
			userPasscode: '',
			passcodeValidationErrors: [],
			isPasscodeEntered: false
		};
	},
	computed: {
		billboard() {
			return this.$store.getters.billboardByCategory(
				this.category.expenseCategory_ID
			);
		},
		isPasscodeRequired() {
			return (
				this.$store.getters.haveToUsePasscodes &&
				this.category.isPasscodeLockedForCurrentStudent &&
				!this.isPasscodeEntered
			);
		}
	},
	watch: {
		userPasscode() {
			this.passcodeValidationErrors = [];
		}
	},
	mounted() {
		if (this.isPasscodeRequired) {
			this.lockedContent();
		} else {
			this.defaultContent();
		}
	},
	methods: {
		closeModal() {
			if (this.isPasscodeRequired) {
				this.userPasscode = '';
				this.passcodeValidationErrors = [];
			}

			this.$emit('closeModal', this.category.expenseCategory_ID);
		},
		openResearch() {
			this.$store.commit('setLoading', true);

			// Default is category
			let routeName = 'category';

			// In some cases this like Auto Loan and Mortgage loan
			// The url is different.
			// If category has key property and this key property is one of applicationloanTypes
			// Then we have to re-route the user to Loan page
			if (
				this.category.key &&
				Object.values(applicationLoanTypes).includes(this.category.key)
			) {
				routeName = 'loan';
			}

			this.$router.push({
				name: routeName,
				params: { category: this.category }
			});
		},
		defaultContent() {
			this.iconName = `expenseCategories/${this.category.assetName}`;
			this.subtitle = this.category.transitionHeading;
			this.content = this.category.transitionSupportText;
		},
		lockedContent() {
			this.iconName = `lock`;
			this.subtitle = this.$t('passcodeIsRequired');
			this.content = this.$t('passcodeHelperText');
		},
		async validatePasscode() {
			this.passcodeValidationErrors = [];
			if (this.userPasscode !== this.category.passcodeForCurrentArea) {
				this.passcodeValidationErrors.push(this.$t('incorrectPasscode'));
				return;
			}

			await ExpenseCategoryService.updatePasscodeRequirement(
				this.category.expenseCategory_ID,
				this.userData.studentId
			);

			this.defaultContent();
			this.isPasscodeEntered = true;

			// Get cateogies again to remove the locker icon since it is shown by a category's property
			this.$store.dispatch('getExpenseCategoryGroups');
		}
	}
};
</script>
<style lang="scss" scoped>
.category-modal {
	::v-deep .modal {
		max-width: 668px;
	}
	::v-deep .category-image {
		border-top-left-radius: 8px;
		border-top-right-radius: 8px;
		height: 400px;
	}

	.close-button {
		z-index: 1;

		@media (any-hover: hover) {
			&:hover {
				cursor: pointer;
				svg {
					fill: $hover-state;
				}
			}
		}

		&:active {
			svg {
				fill: $tapped-state;
			}
		}
		svg {
			fill: $primary-state;
		}
	}

	.passcode {
		width: 100%;
		::v-deep label,
		::v-deep.error-msg {
			text-align: left;
		}
	}

	content {
		padding: 16px 96px 64px 96px;

		.heading-5 {
			margin-top: 8px;
			margin-bottom: 8px;
		}

		.heading-1 {
			margin-bottom: 8px;
		}

		button {
			margin-top: 32px;
		}
	}

	.fade-enter {
		opacity: 0;
	}

	.fade-enter-active {
		transition: opacity 0.5s;
	}

	.fade-leave-active {
		transition: opacity 0.5s;
		opacity: 0;
	}

	.slide-enter-active {
		-webkit-animation: slide-out 0.5s forwards;
		animation: slide-in 0.5s forwards;
	}

	.slide-leave-active {
		-webkit-animation: slide-out 0.5s forwards;
		animation: slide-out 0.5s forwards;
	}

	@keyframes slide-in {
		from {
			-webkit-transform: translateY(50px);
			transform: translateY(50px);
		}
		to {
			-webkit-transform: translateY(0);
			transform: translateY(0);
		}
	}

	@keyframes slide-out {
		from {
			-webkit-transform: translateY(0);
			transform: translateY(0);
		}
		to {
			-webkit-transform: translateY(50px);
			transform: translateY(50px);
		}
	}
}
</style>
